import type { QueryClient } from '@tanstack/react-query';
import { io, type Socket } from 'socket.io-client';

// The realtime client. The server gateway is S8 — this is the browser half, built
// now so every later slice plugs into something that already works.
//
// ADR 0003 and TECH-STACK 2.3–2.5. Four things here are load-bearing:
//
// 1. `transports: ['websocket']` ONLY. Socket.IO's default long-polling-to-
//    WebSocket upgrade requires sticky sessions; without them the polling
//    handshake requests land on different Railway replicas and connections fail
//    intermittently — the worst kind of bug, because it works perfectly on one
//    instance in development. Skipping polling removes the whole class.
//
// 2. A push carries NO record data, ever. Only `{ keys }`, handed to TanStack
//    Query, which refetches through the normal, permission-checked REST endpoint.
//    A payload-carrying push would have to re-implement every field-level
//    permission rule inside the socket layer, and salary would leak to panelists
//    through a channel nobody audits (CLAUDE.md non-negotiable #12).
//
// 3. A blanket `invalidateQueries()` on reconnect. That single line is what makes
//    a dropped message harmless.
//
// 4. "No gateway yet" is a DIFFERENT state from "connection lost". Between now and
//    S8 there is nothing listening, and a client that cannot tell those apart would
//    show a permanent "reconnecting" banner for six slices — training everyone to
//    ignore the one warning that says their screen may be stale. See ABSENT below.

/** The only message shape the client handles. Do not add fields to it. */
export interface InvalidateMessage {
  readonly keys: readonly unknown[];
}

export type ConnectionState =
  /** Nothing is listening and we have never reached a gateway. Not an error —
   *  it is the expected state until S8 builds the server half. */
  | 'absent'
  | 'connecting'
  | 'connected'
  /** We WERE connected and lost it. This is the state worth telling the user about. */
  | 'dropped';

/** Same-origin: the API serves the SPA, so the session cookie rides the upgrade
 *  request automatically and there is no token to handle on the client. */
const SOCKET_PATH = '/socket.io';

const RECONNECT_DELAY_MS = 1_000;
const RECONNECT_DELAY_MAX_MS = 30_000;

/**
 * Consecutive failures before we conclude there is no gateway and stop hammering
 * it. Retrying forever against a server that does not exist fills the console with
 * WebSocket errors and burns battery on a phone for nothing. We resume on window
 * focus, so the moment S8 is deployed the next tab focus finds it.
 *
 * This applies ONLY before the first successful connection. Once a gateway has
 * answered, reconnection is unlimited — a real outage must always be retried.
 */
const ATTEMPTS_BEFORE_ASSUMING_ABSENT = 5;

/** What the gateway's handshake middleware answers when the cookie is not a live session. */
const UNAUTHORISED_MESSAGE = 'unauthorised';

export interface SocketClient {
  readonly socket: Socket;
  subscribe(listener: (state: ConnectionState) => void): () => void;
  getState(): ConnectionState;
  /** Joins a room. Membership is authorised server-side at join time (S8). */
  joinRoom(room: string): void;
  /** Leaves a room a screen joined, when that screen closes (S16: a job's board). */
  leaveRoom(room: string): void;
  /** Connects for a signed-in session. The gateway refuses anyone else. */
  start(): void;
  /** Disconnects and forgets the session's rooms, on sign-out. */
  stop(): void;
  disconnect(): void;
}

export function createSocketClient(queryClient: QueryClient): SocketClient {
  let state: ConnectionState = 'absent';
  let isStarted = false;
  let hasEverConnected = false;
  let consecutiveFailures = 0;
  const rooms = new Set<string>();
  const listeners = new Set<(state: ConnectionState) => void>();

  const setState = (next: ConnectionState): void => {
    if (state === next) return;
    state = next;
    for (const listener of listeners) listener(next);
  };

  const socket: Socket = io({
    path: SOCKET_PATH,
    // See note 1. Never add 'polling' back.
    transports: ['websocket'],
    withCredentials: true,
    reconnection: true,
    reconnectionDelay: RECONNECT_DELAY_MS,
    reconnectionDelayMax: RECONNECT_DELAY_MAX_MS,
    // Connected by `start()` once there is a session. Connecting at page load
    // meant a visitor to /login used up its attempts against a gateway that
    // refuses them, and nothing started it again after sign-in.
    autoConnect: false,
    timeout: 5_000,
  });

  const connectIfStarted = (): void => {
    if (!isStarted || socket.connected || socket.active) return;
    consecutiveFailures = 0;
    // A first connection is not a REconnection: until one has succeeded the state
    // stays `absent`, which reads as online, so a page load never flashes the
    // "reconnecting" and "back online" banners at someone who never went away.
    if (hasEverConnected) setState('connecting');
    socket.connect();
  };

  socket.on('connect', () => {
    const isReconnect = hasEverConnected;
    hasEverConnected = true;
    consecutiveFailures = 0;
    setState('connected');
    // Rejoin every room the app asked for. After a reconnect the server has no
    // memory of this socket's membership, so without this the bell goes quiet.
    for (const room of rooms) socket.emit('join', { room });
    // See note 3. Here rather than on the manager's `reconnect`, which does not
    // fire when `connectIfStarted` reconnects after a server-side close. Anything
    // missed while disconnected is refetched now.
    if (isReconnect) void queryClient.invalidateQueries();
  });

  socket.on('disconnect', (reason) => {
    if (!isStarted) return;
    setState(hasEverConnected ? 'dropped' : 'absent');
    // Socket.IO never reconnects on its own after the server closed the
    // connection. The gateway does that for the 4-hour ceiling, the 5-tab cap and
    // an ended session; a reconnect re-checks the cookie, so an ended session is
    // refused and handled below. Only a visible tab reconnects, so two tabs over
    // the cap cannot keep evicting each other.
    if (reason === 'io server disconnect' && document.visibilityState === 'visible') {
      connectIfStarted();
    }
  });

  socket.on('connect_error', (error: Error) => {
    // The session is gone. Retrying cannot help; a refetch meets the same 401,
    // which the query cache turns into the sign-in screen.
    if (error.message === UNAUTHORISED_MESSAGE) {
      socket.disconnect();
      setState('absent');
      void queryClient.invalidateQueries();
      return;
    }

    consecutiveFailures += 1;
    if (hasEverConnected) {
      setState('dropped');
      return;
    }
    if (consecutiveFailures >= ATTEMPTS_BEFORE_ASSUMING_ABSENT) {
      socket.disconnect();
      setState('absent');
    }
  });

  socket.io.on('reconnect_attempt', () => {
    if (hasEverConnected) setState('connecting');
  });

  socket.on('invalidate', (message: InvalidateMessage) => {
    // Defensive: a malformed hint must not take down the handler.
    if (!message || !Array.isArray(message.keys)) return;
    void queryClient.invalidateQueries({ queryKey: message.keys });
  });

  // Once we have given up, a tab focus is the cheapest possible poll for "has the
  // gateway appeared yet?" — and it costs nothing while the tab is in the
  // background, which is where a tab spends most of its life.
  // It is also how a hidden tab evicted by the server comes back.
  const onFocus = (): void => {
    if (state === 'connected') return;
    connectIfStarted();
  };
  window.addEventListener('focus', onFocus);

  return {
    socket,
    getState: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    joinRoom: (room) => {
      rooms.add(room);
      if (socket.connected) socket.emit('join', { room });
    },
    leaveRoom: (room) => {
      rooms.delete(room);
      if (socket.connected) socket.emit('leave', { room });
    },
    start: () => {
      isStarted = true;
      connectIfStarted();
    },
    stop: () => {
      isStarted = false;
      hasEverConnected = false;
      rooms.clear();
      socket.disconnect();
      setState('absent');
    },
    disconnect: () => {
      window.removeEventListener('focus', onFocus);
      socket.disconnect();
      listeners.clear();
      rooms.clear();
    },
  };
}
